// File: frontend/src/pages/ReportDetail.tsx

import React, { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import useSWR from 'swr';
import { useApi } from '../hooks/useApi';
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import ReactMarkdown from 'react-markdown';
import { UserProfile, Report } from '../types';

// Component Imports
import MermaidDiagram from '../components/MermaidDiagram';
import IOCTable from '../components/IOCTable';
import TTPsTable from '../components/TTPsTable';
import AttackFlow from '../components/AttackFlow';
import MitreHeatmap from '../components/MitreHeatmap';
import DiamondModel from '../components/DiamondModel';
import FiveWhatsTable from '../components/FiveWhatsTable';
import StixDisplay from '../components/StixDisplay';
import AdvancedIOCHandler from '../components/AdvancedIOCHandler';
import CVETable from '../components/CVETable';
import PdfExportButton from '../components/PdfExportButton';
import RelatedReports from '../components/RelatedReports';
import ReportKnowledgeGraph from '../components/ReportKnowledgeGraph';

// MUI Imports
import { Typography, Box, CircularProgress, Alert, Button, Paper, Tabs, Tab, IconButton, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { cyber } from '../theme';
import { Link as LinkIcon } from '@mui/icons-material';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ShareIcon from '@mui/icons-material/Share';

/**
 * A generic component to fetch and display content from a blob storage URL.
 * It handles different content types like Markdown, Mermaid diagrams, and JSON data for IOC tables.
 */
const BlobContent: React.FC<{ url: string | null | undefined, type: 'markdown' | 'json-ioc' | 'mermaid' | 'hybrid-ioc' }> = ({ url, type }) => {
    const { simpleTextFetcher } = useApi();
    const { data, error, isLoading } = useSWR(url, simpleTextFetcher);
    
    if (!url) return <Alert severity="info" sx={{ my: 1 }}>No content available for this section.</Alert>;
    if (isLoading) return <CircularProgress size={24} />;
    if (error) return <Alert severity="error" sx={{ my: 1 }}>Could not load content.</Alert>;

    if (type === 'mermaid') return <MermaidDiagram chart={data || ''} />;
    
    if (type === 'json-ioc') {
        try {
            return <IOCTable iocs={JSON.parse(data || '[]')} />;
        } catch (e) {
            return <Alert severity="error">Failed to parse IOC data.</Alert>;
        }
    }

    if (type === 'markdown') {
        return (
            <Paper sx={{ p: 2, bgcolor: 'background.default', border: `1px solid ${alpha(cyber.primary, 0.15)}`, whiteSpace: 'pre-wrap', fontFamily: cyber.fontMono }}>
                <ReactMarkdown children={data || ''} />
            </Paper>
        );
    }
    return null;
};

const ReportDetail = () => {
    const { reportId } = useParams<{ reportId: string }>();
    const { inProgress } = useMsal();
    const { authenticatedFetcher, poster, authenticatedBlobFetcher } = useApi();
    const [currentTab, setCurrentTab] = useState(0);
    const [isExportingMisp, setIsExportingMisp] = useState(false);

    const handleMispExport = async () => {
        if (!reportId) return;
        setIsExportingMisp(true);
        try {
            const blob = await authenticatedBlobFetcher(`/api/stix/bundle/${reportId}/export?format=misp`);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `misp-event-${reportId}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("MISP export failed", error);
        } finally {
            setIsExportingMisp(false);
        }
    };

    const apiReportUrl = (inProgress === InteractionStatus.None && reportId) ? `/api/reports/${reportId}` : null;
    const apiUserUrl = (inProgress === InteractionStatus.None) ? '/api/users/me' : null;

    const { data: report, error: reportError } = useSWR<Report>(apiReportUrl, authenticatedFetcher);
    const { data: profile, error: profileError, mutate: mutateProfile } = useSWR<UserProfile>(apiUserUrl, authenticatedFetcher);

    const isBookmarked = React.useMemo(() => profile?.bookmarks?.includes(reportId || ''), [profile, reportId]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setCurrentTab(newValue);

    const handleToggleBookmark = async () => {
        if (!reportId) return;
        // Optimistically update the UI
        mutateProfile(
            (p: UserProfile | undefined) => !p ? undefined : ({ ...p, bookmarks: isBookmarked ? p.bookmarks.filter(id => id !== reportId) : [...p.bookmarks, reportId] }),
            false
        );
        try {
            await poster('/api/users/me/bookmarks', { reportId });
        } catch (error) {
            // Revert on failure
            mutateProfile();
            console.error("Failed to update bookmark", error);
        }
    };

    if (!report && (reportError || profileError)) {
        return <Alert severity="error" sx={{ my: 2 }}>{`Failed to fetch details: ${reportError?.message || profileError?.message}`}</Alert>;
    }
    if (!report || inProgress !== InteractionStatus.None) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <Button component={RouterLink} to="/" sx={{ mb: 2 }}>&larr; Back to Dashboard</Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 0 }}>{report?.title || 'Report Detail'}</Typography>
                <Tooltip title={isBookmarked ? "Remove Bookmark" : "Add Bookmark"}>
                    <span>
                        <IconButton onClick={handleToggleBookmark} color={isBookmarked ? 'primary' : 'default'} disabled={!profile}>
                            {isBookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Source:{' '}
                <Box component="span" sx={{ color: cyber.accent.cyan, fontFamily: cyber.fontMono, fontSize: '0.9em' }}>
                    {report?.sourceFeedName}
                </Box>
                {' '}| Published:{' '}
                <Box component="span" sx={{ fontFamily: cyber.fontMono, fontSize: '0.9em' }}>
                    {report?.publishedDate ? new Date(report.publishedDate).toLocaleDateString() : 'N/A'}
                </Box>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, mb: 2, flexWrap: 'wrap' }}>
                {report?.articleUrl && <Button href={report.articleUrl} target="_blank" rel="noopener noreferrer" startIcon={<LinkIcon />}>View Original Source</Button>}
                <PdfExportButton report={report} />
                <Tooltip title="Download this report's IOCs and CVEs as a MISP Event JSON, importable into any MISP instance">
                    <span>
                        <Button
                            onClick={handleMispExport}
                            disabled={isExportingMisp}
                            startIcon={isExportingMisp ? <CircularProgress size={16} /> : <ShareIcon />}
                            sx={{ textTransform: 'none' }}
                        >
                            {isExportingMisp ? 'Exporting…' : 'Export MISP Event'}
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            <Paper sx={{ mt: 2, width: '100%' }}>
                <Tabs 
                    value={currentTab} 
                    onChange={handleTabChange} 
                    variant="scrollable" 
                    scrollButtons="auto" 
                    allowScrollButtonsMobile
                    sx={{
                        borderBottom: 1, 
                        borderColor: 'divider',
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.95rem',
                            minHeight: '56px',
                        }
                    }}
                >
                    <Tab label="Intel Graph" />
                    <Tab label="Diamond Model" />
                    <Tab label="AI Summary" />
                    <Tab label="TI Mindmap" />
                    <Tab label="IOCs" />
                    <Tab label="CVEs" />
                    <Tab label="TTP Catalog" />
                    <Tab label="Attack Flow" />
                    <Tab label="5W Context" />
                    <Tab label="ATT&CK Heatmap" />
                    <Tab label="Knowledge Graph" />
                    <Tab label="Source Report" />
                </Tabs>
                
                <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, minHeight: '300px' }}>
                    {currentTab === 0 && <StixDisplay stixUrl={report?.blobPathStixBundle ?? undefined} />}
                    {currentTab === 1 && <DiamondModel stixUrl={report?.blobPathStixBundle ?? undefined} />}
                    {currentTab === 2 && <BlobContent url={report?.blobPathSummary} type="markdown" />}
                    {currentTab === 3 && (
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <Paper
                                sx={{
                                    p: { xs: 2, md: 2.5 },
                                    borderRadius: 3,
                                    border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                                    background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.background.paper, 0.82)} 100%)`,
                                    backdropFilter: 'blur(16px)',
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
                                    Threat topology view
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                    This view turns the report into a navigable storyline. Focus one branch to isolate the most relevant cluster, inspect related nodes, and export the active view directly as PNG or SVG.
                                </Typography>
                            </Paper>
                            <BlobContent url={report?.blobPathMindmapCode} type="mermaid" />
                        </Box>
                    )}
                    {currentTab === 4 && <AdvancedIOCHandler url={report?.blobPathAdvancedIOCs} reportId={reportId ?? 'unknown'} />}
                    {currentTab === 5 && <CVETable reportId={reportId ?? 'unknown'} />}
                    {currentTab === 6 && <TTPsTable tableUrl={report?.blobPathTTPsTable ?? undefined} />}
                    {currentTab === 7 && <AttackFlow stixUrl={report?.blobPathStixBundle ?? undefined} />}
                    {currentTab === 8 && <FiveWhatsTable fiveWhatsUrl={report?.blobPathFiveWhats ?? undefined} />}
                    {currentTab === 9 && <MitreHeatmap mitreLayerUrl={report?.blobPathMitreLayer ?? undefined} />}
                    {currentTab === 10 && <ReportKnowledgeGraph reportId={reportId ?? 'unknown'} />}
                    {currentTab === 11 && <BlobContent url={report?.blobPathRawArticle} type="markdown" />}
                </Box>
            </Paper>

            {reportId && <RelatedReports reportId={reportId} />}
        </Box>
    );
};

export default ReportDetail;